import { TranscriptionWordItemDto } from '@app/modules/fragments/fragment.types';

/**
 * Splitting rules for transcript blocks.
 * Keep these values at the top so product/UX can tweak block sizing behavior quickly.
 */
export const TRANSCRIPTION_BLOCK_SPLIT_RULES = {
  /** Never close punctuation/gap splits before this duration (seconds). */
  minDurationSec: 10,
  /** Hard cap: force split when current sub-block reaches/exceeds this duration (seconds). */
  maxDurationSec: 20,
  /** Prefer a split when the pause between consecutive words reaches this threshold (seconds). */
  gapThresholdSec: 2,
  /** Optional hard cap for very dense speech segments. */
  maxWordsPerBlock: Infinity,
} as const;

const SENTENCE_END_PUNCTUATION_REGEX = /[.!?]+["')\]]?$/;

type SegmentRange = {
  start: number;
  end: number;
};

export type SplitTranscriptionSubBlock = {
  segment: SegmentRange;
  words: TranscriptionWordItemDto[];
};

/**
 * Returns true when a token ends a sentence-like unit.
 */
function isSentenceBoundaryWord(text: string): boolean {
  return SENTENCE_END_PUNCTUATION_REGEX.test(text.trim());
}

/**
 * Builds split groups preserving word order, using punctuation, pauses, and max duration rules.
 */
function splitWordsInOrder(words: TranscriptionWordItemDto[]): TranscriptionWordItemDto[][] {
  if (words.length === 0) {
    return [];
  }

  const sortedWords = [...words].sort((a, b) => {
    const startDiff = a.start - b.start;
    return startDiff !== 0 ? startDiff : a.end - b.end;
  });

  const groups: TranscriptionWordItemDto[][] = [];
  let groupStartIndex = 0;

  for (let index = 0; index < sortedWords.length; index++) {
    const currentWord = sortedWords[index];
    const nextWord = sortedWords[index + 1];
    const isLastWord = nextWord === undefined;
    const groupStartWord = sortedWords[groupStartIndex];
    const groupDurationSec = Math.max(0, currentWord.end - groupStartWord.start);
    const wordCount = index - groupStartIndex + 1;
    const pauseAfterCurrentSec = nextWord ? Math.max(0, nextWord.start - currentWord.end) : 0;

    const shouldForceByDuration =
      groupDurationSec >= TRANSCRIPTION_BLOCK_SPLIT_RULES.maxDurationSec;
    const shouldForceByWordCount = wordCount >= TRANSCRIPTION_BLOCK_SPLIT_RULES.maxWordsPerBlock;
    const shouldSplitByGap =
      groupDurationSec >= TRANSCRIPTION_BLOCK_SPLIT_RULES.minDurationSec &&
      pauseAfterCurrentSec >= TRANSCRIPTION_BLOCK_SPLIT_RULES.gapThresholdSec;
    const shouldSplitByPunctuation =
      groupDurationSec >= TRANSCRIPTION_BLOCK_SPLIT_RULES.minDurationSec &&
      isSentenceBoundaryWord(currentWord.word);

    if (
      isLastWord ||
      shouldForceByDuration ||
      shouldForceByWordCount ||
      shouldSplitByGap ||
      shouldSplitByPunctuation
    ) {
      groups.push(sortedWords.slice(groupStartIndex, index + 1));
      groupStartIndex = index + 1;
    }
  }

  return groups;
}

/**
 * Splits one diarization segment into smaller transcription sub-blocks.
 * Segment boundaries are clamped to the parent segment, while words keep original timings.
 */
export function splitDiarizationBlocks(
  segment: SegmentRange,
  words: TranscriptionWordItemDto[],
): SplitTranscriptionSubBlock[] {
  const groups = splitWordsInOrder(words);

  return groups
    .map((group) => {
      const firstWord = group[0];
      const lastWord = group[group.length - 1];
      const start = Math.max(segment.start, firstWord.start);
      const end = Math.min(segment.end, lastWord.end);

      if (end <= start) {
        return null;
      }

      return {
        segment: {
          start,
          end,
        },
        words: group,
      };
    })
    .filter((subBlock): subBlock is SplitTranscriptionSubBlock => subBlock !== null);
}
